import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useRealtimeEvent } from '@/context/NotificationContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Search,
  Plus,
  FileText,
  Upload,
  Eye,
  Download,
  UserCheck,
  RefreshCw,
  Clock,
  Play,
  AlertCircle,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import brandLogo from '@/assets/logo.png';
import { generatePrescriptionPDF } from '@/lib/generatePrescriptionPDF';
import { toast } from 'sonner';
import ErpSyncBadge from '@/components/prescriptions/ErpSyncBadge';

const STATUS_CONFIG = {
  PENDING: { label: 'Pending', className: 'bg-amber-100 text-amber-800 border-amber-300' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-800 border-blue-300' },
  WAITING: { label: 'Waiting', className: 'bg-purple-100 text-purple-800 border-purple-300' },
  COMPLETED: { label: 'Completed', className: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELLED: { label: 'Cancelled', className: 'bg-slate-200 text-slate-700 border-slate-300' }
};

export const PrescriptionManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);

  // Assign modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedRx, setSelectedRx] = useState(null);
  const [users, setUsers] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [savingAssign, setSavingAssign] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);

  // Fetch operational users for assignment dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/users', { params: { status: 'ACTIVE', role: 'USER' } });
        const list = res.data?.data || res.data?.items || (Array.isArray(res.data) ? res.data : []);
        setUsers(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Failed to fetch operational users:', err);
      }
    };
    fetchUsers();
  }, []);

  const fetchPrescriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        page_size: pageSize,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (statusFilter === 'UNASSIGNED') {
        params.unassigned = true;
        params.unassigned_only = true;
      } else if (statusFilter !== 'ALL') {
        params.workflow_status = statusFilter;
        params.status = statusFilter;
      }

      const res = await api.get('/prescriptions', { params });
      if (res.data) {
        setPrescriptions(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
      toast.error('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchPrescriptions();
  }, [fetchPrescriptions]);

  // Real-time automatic updates across the application (zero manual refresh needed)
  useRealtimeEvent(
    [
      'prescription.created',
      'prescription.assigned',
      'prescription.updated',
      'prescription.status_changed',
      'prescription.note_added',
    ],
    useCallback(() => {
      fetchPrescriptions();
    }, [fetchPrescriptions])
  );

  const toDataURL = (url) =>
    fetch(url)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );

  const handleGenerate = async (prescription) => {
    if (!prescription) return;

    let patientInfo = {
      name: prescription.patient_name || prescription.patientName || 'Patient',
      age: prescription.age || '—',
      sex: prescription.gender || prescription.sex || '—',
      address: prescription.address || '—',
      phone: '',
      email: ''
    };

    if (prescription.patient_id || prescription.patientId) {
      try {
        const pid = prescription.patient_id || prescription.patientId;
        const res = await api.get(`/patients/${pid}`);
        if (res.data) {
          patientInfo = {
            ...patientInfo,
            name: res.data.name || patientInfo.name,
            age: res.data.age || patientInfo.age,
            sex: res.data.gender || patientInfo.sex,
            address: res.data.address || patientInfo.address,
            phone: res.data.phone || '',
            email: res.data.email || ''
          };
        }
      } catch (e) {
        console.warn('Could not fetch patient details for PDF', e);
      }
    }

    if (!patientInfo.name) {
      toast.error('Patient name is required to generate PDF');
      return;
    }

    setGeneratingId(prescription.id);
    try {
      const logoDataUrl = await toDataURL(brandLogo);

      const medicinesArray = Array.isArray(prescription.medicines)
        ? prescription.medicines
        : prescription.medicines
          ? String(prescription.medicines)
              .split(',')
              .map((m) => m.trim())
              .filter(Boolean)
          : [];

      const medicinesStructured = medicinesArray.map((m) => {
        if (typeof m === 'string') {
          return { name: m, dosage: '', frequency: '', duration: '' };
        }
        return {
          name: m.name || m.medicineName || '',
          dosage: m.dosage || '',
          frequency: m.frequency || '',
          duration: m.duration || ''
        };
      });

      await generatePrescriptionPDF({
        patient_name: patientInfo.name,
        patient_id: prescription.patient_id || prescription.patientId || '—',
        patient_age: patientInfo.age,
        patient_gender: patientInfo.sex,
        patient_phone: patientInfo.phone,
        patient_email: patientInfo.email,
        patient_address: patientInfo.address,
        visit_date: prescription.visit_date || prescription.date || '',
        diet: prescription.diet || prescription.Diet || '',
        lifestyle: prescription.lifestyle || prescription.Lifestyle || '',
        medicines: medicinesStructured,
        notes: prescription.notes || '',
        follow_up_date: prescription.follow_up_date || prescription.followUpDate || '',
        clinic: {
          logo: logoDataUrl,
          name: 'Sanjeevani'
        },
        filename: `Prescription-${patientInfo.name}-${prescription.id}.pdf`
      });

      toast.success('Prescription PDF downloaded');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error(error.message || 'Unable to generate PDF');
    } finally {
      setGeneratingId(null);
    }
  };

  const openAssignModal = (rx) => {
    setSelectedRx(rx);
    setAssigneeId(rx.assigned_user_id ? String(rx.assigned_user_id) : '');
    setAssignModalOpen(true);
  };

  const handleSaveAssignment = async () => {
    if (!selectedRx) return;
    setSavingAssign(true);
    try {
      const payload = {
        assigned_user_id: assigneeId ? parseInt(assigneeId, 10) : null
      };
      await api.post(`/prescriptions/${selectedRx.id}/assign`, payload);
      toast.success(assigneeId ? 'Prescription assigned successfully' : 'Prescription unassigned');
      setAssignModalOpen(false);
      fetchPrescriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update assignment');
    } finally {
      setSavingAssign(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  const filterTabs = [
    { key: 'ALL', label: 'All' },
    { key: 'PENDING', label: 'Pending', icon: Clock },
    { key: 'IN_PROGRESS', label: 'In Progress', icon: Play },
    { key: 'WAITING', label: 'Waiting', icon: AlertCircle },
    { key: 'COMPLETED', label: 'Completed', icon: CheckCircle2 },
    { key: 'CANCELLED', label: 'Cancelled', icon: XCircle },
    { key: 'UNASSIGNED', label: 'Unassigned', icon: UserCheck }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-start justify-between gap-3 w-full sm:w-auto">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold mb-0.5 sm:mb-1">Prescription Management</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Manage prescriptions, workflow dispatching, and fulfillment
            </p>
          </div>
          {/* Mobile Icon-only Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchPrescriptions}
            disabled={loading}
            className="sm:hidden h-9 w-9 flex-shrink-0"
            aria-label="Refresh prescriptions"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
          <Button asChild variant="outline" size="sm" className="h-10 flex-1 sm:flex-initial">
            <Link to="/admin/prescriptions/upload">
              <Upload className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Upload</span>
              <span className="sm:hidden">Upload</span>
            </Link>
          </Button>
          <Button asChild className="bg-emerald-700 hover:bg-emerald-800 text-white h-10 px-3 sm:px-4 flex-1 sm:flex-initial">
            <Link to="/admin/prescriptions/new">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Create New</span>
              <span className="sm:hidden">New</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* ── Workflow Filter Tabs (scrollable strip) ── */}
      <div className="filter-strip border-b pb-3">
        {filterTabs.map((tab) => {
          const Icon = tab.icon;
          const isTabActive = statusFilter === tab.key;
          return (
            <Button
              key={tab.key}
              variant={isTabActive ? 'default' : 'ghost'}
              size="sm"
              onClick={() => { setStatusFilter(tab.key); setPage(1); }}
              className={`filter-strip-item gap-1.5 h-9 whitespace-nowrap ${isTabActive ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'text-slate-600'}`}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {tab.label}
            </Button>
          );
        })}
      </div>

      {/* ── Search + Refresh ── */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search patient, phone, or ID..."
            className="pl-9 h-11"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          />
        </div>
        {/* Desktop Refresh Button */}
        <Button
          variant="outline"
          onClick={fetchPrescriptions}
          disabled={loading}
          className="hidden sm:inline-flex h-11 gap-1.5 flex-shrink-0"
          aria-label="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </Button>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{total} prescription{total !== 1 ? 's' : ''}</span>
        {totalPages > 1 && <span>Page {page} of {totalPages}</span>}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <RefreshCw className="h-6 w-6 animate-spin text-emerald-600 mr-3" />
          Loading prescriptions...
        </div>
      )}

      {/* ── MOBILE card list (hidden md+) ── */}
      {!loading && (
        <div className="md:hidden space-y-3">
          {prescriptions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground bg-muted/30 rounded-xl">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No prescriptions found</p>
            </div>
          ) : prescriptions.map((prescription) => {
            const statusMeta = STATUS_CONFIG[prescription.workflow_status] || {
              label: prescription.workflow_status || 'Pending',
              className: 'bg-slate-100 text-slate-700'
            };
            const isUnassigned = !prescription.assigned_user_id;
            const patientName = prescription.patient_name || prescription.patientName || 'Patient';
            return (
              <div
                key={prescription.id}
                className="mobile-card cursor-pointer"
                onClick={() => navigate(`/admin/prescriptions/${prescription.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/admin/prescriptions/${prescription.id}`)}
              >
                {/* Row 1: patient + status */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="bg-emerald-50 p-2 rounded-lg text-emerald-700 flex-shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{patientName}</p>
                      {prescription.phone && (
                        <p className="text-xs text-muted-foreground">{prescription.phone}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                    <Badge className={`border text-xs ${statusMeta.className}`}>
                      {statusMeta.label}
                    </Badge>
                    <ErpSyncBadge
                      status={prescription.erp_sync_status}
                      invoiceNo={prescription.erp_invoice_no}
                    />
                  </div>
                </div>

                {/* Row 2: metadata */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
                  <span className="font-mono">#{prescription.id}</span>
                  <span>{prescription.visit_date || prescription.date || '—'}</span>
                  <Badge
                    variant={(prescription.type || 'Digital') === 'Digital' ? 'default' : 'secondary'}
                    className="text-[10px] h-4 px-1.5"
                  >
                    {prescription.type || 'Digital'}
                  </Badge>
                </div>

                {/* Row 3: assignment + actions */}
                <div className="flex items-center justify-between gap-2 pt-2.5 border-t border-border/50">
                  <div className="flex-1 min-w-0">
                    {isUnassigned ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); openAssignModal(prescription); }}
                        className="text-xs text-amber-700 border border-dashed border-amber-300 rounded-md px-2.5 py-1.5 hover:bg-amber-50 transition-colors"
                      >
                        + Assign Staff
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); openAssignModal(prescription); }}
                        className="text-left"
                      >
                        <p className="text-xs font-medium text-slate-700 hover:text-emerald-700 underline decoration-dotted truncate">
                          {prescription.assigned_user_name || `User #${prescription.assigned_user_id}`}
                        </p>
                        <p className="text-[10px] text-muted-foreground">Tap to reassign</p>
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-10 w-10 p-0"
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/prescriptions/${prescription.id}`); }}
                      aria-label="View prescription"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (prescription.file_url) window.open(prescription.file_url, '_blank');
                        else handleGenerate(prescription);
                      }}
                      disabled={generatingId === prescription.id}
                      aria-label="Download PDF"
                    >
                      {generatingId === prescription.id
                        ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        : <Download className="h-4 w-4" />
                      }
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── DESKTOP table (md+) ── */}
      {!loading && (
        <Card className="shadow-card hidden md:block">
          <CardHeader className="border-b border-border py-4 px-6 flex flex-row items-center justify-between">
            <CardTitle className="font-serif text-xl">Prescriptions ({total})</CardTitle>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Visit Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Workflow Status</TableHead>
                    <TableHead>ERP Sync</TableHead>
                    <TableHead>Assigned Staff</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prescriptions.length > 0 ? prescriptions.map((prescription) => {
                    const statusMeta = STATUS_CONFIG[prescription.workflow_status] || {
                      label: prescription.workflow_status || 'Pending',
                      className: 'bg-slate-100 text-slate-700'
                    };
                    const isUnassigned = !prescription.assigned_user_id;
                    return (
                      <TableRow key={prescription.id}>
                        <TableCell className="font-mono text-xs text-slate-500">#{prescription.id}</TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="bg-emerald-50 p-2 rounded-lg text-emerald-700">
                              <FileText className="h-4 w-4" />
                            </div>
                            <div>
                              <div>{prescription.patient_name || prescription.patientName || 'Patient'}</div>
                              {prescription.phone && (
                                <div className="text-xs text-muted-foreground">{prescription.phone}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{prescription.visit_date || prescription.date || '—'}</TableCell>
                        <TableCell>
                          <Badge variant={(prescription.type || 'Digital') === 'Digital' ? 'default' : 'secondary'}>
                            {prescription.type || 'Digital'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`border text-xs ${statusMeta.className}`}>{statusMeta.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <ErpSyncBadge
                            status={prescription.erp_sync_status}
                            invoiceNo={prescription.erp_invoice_no}
                          />
                        </TableCell>
                        <TableCell>
                          {isUnassigned ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs border-dashed text-amber-700 border-amber-300 hover:bg-amber-50"
                              onClick={() => openAssignModal(prescription)}
                            >
                              + Assign Staff
                            </Button>
                          ) : (
                            <button onClick={() => openAssignModal(prescription)} className="text-left group cursor-pointer" title="Click to reassign">
                              <div className="text-xs font-medium text-slate-800 group-hover:text-emerald-700 underline decoration-dotted">
                                {prescription.assigned_user_name || `User #${prescription.assigned_user_id}`}
                              </div>
                              <div className="text-[10px] text-muted-foreground">Click to change</div>
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1.5">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/prescriptions/${prescription.id}`)} title="View details">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (prescription.file_url) window.open(prescription.file_url, '_blank');
                                else handleGenerate(prescription);
                              }}
                              disabled={generatingId === prescription.id}
                              title="Download PDF"
                            >
                              {generatingId === prescription.id ? '...' : <Download className="h-4 w-4" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                        No prescriptions found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            {/* Desktop pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-border">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
                <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mobile pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between md:hidden pb-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="h-10">Previous</Button>
          <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="h-10">Next</Button>
        </div>
      )}

      {/* ── Assignment Modal ── */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="w-full max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Prescription #{selectedRx?.id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <p className="text-sm text-slate-500 mb-1">Patient</p>
              <p className="font-semibold text-slate-800">
                {selectedRx?.patient_name || selectedRx?.patientName || 'Patient'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2" htmlFor="assignee-select">
                Assign to Operational Staff
              </label>
              <select
                id="assignee-select"
                className="w-full h-12 px-3 rounded-md border border-slate-300 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
              >
                <option value="">— Unassigned (Return to Pool) —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} (@{u.username || u.email})</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Only active operational users can be assigned.</p>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
            <Button variant="outline" onClick={() => setAssignModalOpen(false)} className="h-11 sm:h-10">Cancel</Button>
            <Button onClick={handleSaveAssignment} disabled={savingAssign} className="bg-emerald-700 hover:bg-emerald-800 text-white h-11 sm:h-10">
              {savingAssign ? 'Saving...' : 'Save Assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrescriptionManagement;
